// Utilities for exporting/sharing the same "zoomed-in" crop the player sees.
//
// Important limitation:
// - If `imageUrl` is hosted on a domain that does NOT allow CORS, drawing it to a canvas
//   will "taint" the canvas and exporting will fail (SecurityError).
// - In that case, callers should fall back to sharing the image URL (not the crop).
//
// The in-game zoom effect is:
// - an `img` centered inside a square `.image-container` with `overflow: hidden`
// - the `img` element's box is scaled using width/height = `${zoomLevel}%`
// - the image content is `object-fit: cover` with a random pan offset (zoomOrigin)
//
// With a square container, this is equivalent to:
// 1) take the centered square crop of the source image (object-fit: cover to square)
// 2) take a further crop by the zoom factor, offset by the interpolated pan position

import { getCanvasCropForZoom } from './zoomPosition';

function loadImageForCanvas(imageUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Best effort: enables canvas export when the server sends proper CORS headers.
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
}

function canvasToBlob(canvas, type = 'image/png', quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to export image'));
          return;
        }
        resolve(blob);
      },
      type,
      quality
    );
  });
}

export async function createZoomedCropBlob({
  imageUrl,
  zoomLevel = 100,
  zoomOrigin = null,
  outputSize = 768,
  type = 'image/png',
  quality,
} = {}) {
  if (!imageUrl) throw new Error('Missing imageUrl');

  const img = await loadImageForCanvas(imageUrl);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) throw new Error('Image has invalid dimensions');

  // 1) Center square (object-fit: cover for a square box).
  const square = Math.min(w, h);
  const baseSx = (w - square) / 2;
  const baseSy = (h - square) / 2;

  // 2) Zoom crop with pan offset.
  const { cropX, cropY, cropSize } = getCanvasCropForZoom(square, zoomLevel, zoomOrigin);
  const sx = baseSx + cropX;
  const sy = baseSy + cropY;

  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, cropSize, cropSize, 0, 0, outputSize, outputSize);

  return await canvasToBlob(canvas, type, quality);
}

// ---- Share card: renders zoomed photo + emoji results into one image ----

const STATUS_COLORS = {
  correct: '#538d4e',
  present: '#b59f3b',
  absent: '#3a3a3c',
};

/**
 * Render a share card: zoomed photo on top, results grid + text below.
 *
 * Layout (all values in canvas pixels):
 * ┌──────────────────────┐
 * │    [zoomed photo]     │  photoSize x photoSize
 * │                       │
 * │  gameName             │  header text
 * │  score/max            │  score text
 * │                       │
 * │  🟩🟨⬛⬛⬛            │  colored squares grid
 * │  🟩🟩🟩🟩🟩            │
 * │                       │
 * │  Play free at url     │  footer text
 * └──────────────────────┘
 */
export async function createShareCardBlob({
  imageUrl,
  zoomLevel = 100,
  zoomOrigin = null,
  gameName = 'Game',
  solution = '',
  statusRows = [],
  maxGuesses = 5,
  cardWidth = 512,
} = {}) {
  if (!imageUrl) throw new Error('Missing imageUrl');

  const img = await loadImageForCanvas(imageUrl);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) throw new Error('Image has invalid dimensions');

  // ---- Compute zoomed crop region ----
  const square = Math.min(iw, ih);
  const baseSx = (iw - square) / 2;
  const baseSy = (ih - square) / 2;
  const { cropX, cropY, cropSize: zoomSquare } = getCanvasCropForZoom(square, zoomLevel, zoomOrigin);
  const sx = baseSx + cropX;
  const sy = baseSy + cropY;

  // ---- Layout constants ----
  const pad = Math.round(cardWidth * 0.05);       // outer padding
  const photoSize = cardWidth - pad * 2;           // photo fills width minus padding
  const photoRadius = Math.round(cardWidth * 0.02);
  const cellSize = Math.round(cardWidth * 0.075);  // each grid square
  const cellGap = Math.round(cellSize * 0.15);
  const cellRadius = Math.round(cellSize * 0.12);
  const gridRows = statusRows.length;

  // Text sizes (proportional to card width)
  const headerFontSize = Math.round(cardWidth * 0.048);
  const scoreFontSize = Math.round(cardWidth * 0.065);
  const footerFontSize = Math.round(cardWidth * 0.032);

  // ---- Compute total height ----
  const gapAfterPhoto = Math.round(pad * 0.8);
  const gapAfterHeader = Math.round(pad * 0.15);
  const gapAfterScore = Math.round(pad * 0.6);
  const gridHeight = gridRows > 0
    ? gridRows * cellSize + (gridRows - 1) * cellGap
    : 0;
  const gapAfterGrid = Math.round(pad * 0.6);
  const footerHeight = footerFontSize + pad;

  const cardHeight =
    pad +
    photoSize +
    gapAfterPhoto +
    headerFontSize +
    gapAfterHeader +
    scoreFontSize +
    gapAfterScore +
    gridHeight +
    gapAfterGrid +
    footerHeight;

  // ---- Create canvas ----
  const canvas = document.createElement('canvas');
  canvas.width = cardWidth;
  canvas.height = cardHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  // Background
  ctx.fillStyle = '#121213';
  ctx.fillRect(0, 0, cardWidth, cardHeight);

  // ---- Draw zoomed photo (rounded corners) ----
  let y = pad;
  ctx.save();
  roundRect(ctx, pad, y, photoSize, photoSize, photoRadius);
  ctx.clip();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, zoomSquare, zoomSquare, pad, y, photoSize, photoSize);
  ctx.restore();

  y += photoSize + gapAfterPhoto;

  // ---- Header: game name ----
  ctx.fillStyle = '#ffffff';
  ctx.font = `700 ${headerFontSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(gameName, cardWidth / 2, y);

  y += headerFontSize + gapAfterHeader;

  // ---- Score ----
  const winRowIdx = statusRows.findIndex(
    (row) => row.every((s) => s === 'correct')
  );
  const score = winRowIdx >= 0 ? String(winRowIdx + 1) : 'X';

  ctx.fillStyle = '#ffffff';
  ctx.font = `700 ${scoreFontSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.fillText(`${score}/${maxGuesses}`, cardWidth / 2, y);

  y += scoreFontSize + gapAfterScore;

  // ---- Emoji grid (colored squares) ----
  const gridWidth = 5 * cellSize + 4 * cellGap;
  const gridX = (cardWidth - gridWidth) / 2;

  statusRows.forEach((row, rowIdx) => {
    row.forEach((status, colIdx) => {
      const cx = gridX + colIdx * (cellSize + cellGap);
      const cy = y + rowIdx * (cellSize + cellGap);
      ctx.fillStyle = STATUS_COLORS[status] || STATUS_COLORS.absent;
      roundRect(ctx, cx, cy, cellSize, cellSize, cellRadius);
      ctx.fill();
    });
  });

  y += gridHeight + gapAfterGrid;

  // ---- Footer ----
  ctx.fillStyle = '#818384';
  ctx.font = `400 ${footerFontSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.fillText('Play free at zoomed-in.com', cardWidth / 2, y);

  return await canvasToBlob(canvas, 'image/png');
}

/** Helper: draw a rounded rect path (does NOT fill/stroke -- caller does). */
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

export async function shareOrDownloadImageBlob({
  blob,
  fileName = 'zoomed-image.png',
  shareTitle = '',
  shareText = '',
} = {}) {
  if (!blob) throw new Error('Missing blob');

  const file = new File([blob], fileName, { type: blob.type || 'image/png' });

  // Web Share (best UX on mobile).
  if (navigator?.share && navigator?.canShare?.({ files: [file] })) {
    await navigator.share({
      title: shareTitle,
      text: shareText,
      files: [file],
    });
    return { method: 'share' };
  }

  // Fallback: download.
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return { method: 'download' };
}

